import { ReactElement, useState } from 'react'
import { CommentPlanFilter, commentPlanFilterLabels, commentPlanFilters } from '@constants'
import { MenuItem, Select, Stack, Typography } from '@mui/material'
import {
  useCommentFilterStore,
  useModelStore,
  usePlanImageCardStore,
  usePlanTableStore,
} from '@editorStores'
import PlanAreaSettingsPopover from './PlanAreaSettingsPopover'
import StoreyTypeToggleButton from './StoreyTypeToggleButton'

/**
 * The "Plan" column: global visibility toggles for comments, plan tables and
 * plan images, a dropdown to pick the single plan category (Aussteifungsplan /
 * Positionsplan / Lastplan / Notiz) whose comments, tables and images are shown
 * on the currently visible storeys, and the Planbereich toggle. Rendered as its
 * own column to the right of the storey filters.
 */
const CommentPlanFilterSelect = (): ReactElement => {
  const selectedPlan = useCommentFilterStore(state => state.selectedPlan)
  const setSelectedPlan = useCommentFilterStore(state => state.setSelectedPlan)
  const showComments = useModelStore(state => state.showComments)
  const setShowComments = useModelStore(state => state.setShowComments)
  const showPlanArea = useModelStore(state => state.showPlanArea)
  const setShowPlanArea = useModelStore(state => state.setShowPlanArea)
  const showRealWallThickness = useModelStore(state => state.showRealWallThickness)
  const setShowRealWallThickness = useModelStore(state => state.setShowRealWallThickness)
  const showTables = usePlanTableStore(state => state.isVisible)
  const setShowTables = usePlanTableStore(state => state.setIsVisible)
  const showImages = usePlanImageCardStore(state => state.isVisible)
  const setShowImages = usePlanImageCardStore(state => state.setIsVisible)

  const [planAreaSettingsAnchorEl, setPlanAreaSettingsAnchorEl] = useState<null | HTMLElement>(null)

  return (
    <Stack
      direction="column"
      spacing={0.5}
      alignItems="flex-start"
      sx={{ minWidth: 0, width: '100%', py: 0.125, maxWidth: 'min-content' }}
    >
      <Typography fontSize="small">Plan</Typography>
      <Select
        size="small"
        variant="standard"
        value={selectedPlan}
        onChange={event => setSelectedPlan(event.target.value as CommentPlanFilter)}
        // Fixed width that fits the widest option ("Aussteifungsplan") so the column doesn't jump
        // when the selection changes.
        sx={{ minWidth: 120, '& .MuiSelect-select': { py: 0.25, fontSize: '0.75rem' } }}
        // Render the menu inline (not in a body portal): the storey-controls panel
        // sits in a high stacking context that otherwise renders over the portaled
        // menu. Mirrors IFCFileSelect / StoreyImageUpload in the same panel.
        MenuProps={{ disablePortal: true }}
        data-cy="comment-plan-filter"
      >
        {commentPlanFilters.map(plan => (
          <MenuItem
            key={plan}
            value={plan}
            dense
            onClick={event => event.stopPropagation()}
            data-cy={`comment-plan-filter-${plan}`}
          >
            {commentPlanFilterLabels[plan]}
          </MenuItem>
        ))}
      </Select>
      <StoreyTypeToggleButton
        value="comments"
        label="Kommentare"
        selected={showComments}
        onToggle={() => setShowComments(!showComments)}
        dataCy="btn-comments-visibility"
      />
      <StoreyTypeToggleButton
        value="plan-tables"
        label="Tabellen"
        selected={showTables}
        onToggle={() => setShowTables(!showTables)}
        dataCy="btn-plan-tables-visibility"
      />
      <StoreyTypeToggleButton
        value="plan-images"
        label="Bilder"
        selected={showImages}
        onToggle={() => setShowImages(!showImages)}
        dataCy="btn-plan-images-visibility"
      />
      <StoreyTypeToggleButton
        value="real-wall-thickness"
        label="Reale Wanddicke (2D)"
        selected={showRealWallThickness}
        onToggle={() => setShowRealWallThickness(!showRealWallThickness)}
        dataCy="btn-real-wall-thickness-visibility"
      />
      <StoreyTypeToggleButton
        value="plan-area"
        label="Planbereich"
        selected={showPlanArea}
        onToggle={() => setShowPlanArea(!showPlanArea)}
        onClickSettings={event => setPlanAreaSettingsAnchorEl(event.currentTarget)}
        settingsTooltip="Einstellungen Planbereich"
        dataCy="btn-plan-area-visibility"
        settingsDataCy="btn-plan-area-settings"
      />
      {planAreaSettingsAnchorEl ? (
        <PlanAreaSettingsPopover
          anchorEl={planAreaSettingsAnchorEl}
          onClose={() => setPlanAreaSettingsAnchorEl(null)}
        />
      ) : null}
    </Stack>
  )
}

export default CommentPlanFilterSelect
